import {
  ColumnDefinition,
  getDefaultColumnDescription
} from 'components/TableColumn/TableColumn';
import { commaDecorator } from 'components/ReportTable/ReportDataHelper';
import i18n from 'i18n';
import moment from 'moment-timezone';
import {
  csvHourLabelValueMap,
  hourLabelValueMap,
  KEY_COLUMN
} from './OutdoorDeviceReportModel';
import { OutdoorDeviceReportTableRecord } from 'core/report/ReportData';
import styles from './outdoorDeviceReport.module.scss';
import { DataProvider, OutdoorDeviceReportCsvContent } from './DataProvider';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';

export class HourDataProvider implements DataProvider {
  constructor(protected deliveryUnit?: DeliveryUnit) {}

  protected get deliveryTargetNameHeader(): string {
    return this.deliveryUnit === DeliveryUnit.DEVICE
      ? i18n.t('outdoorDeviceReport.headers.deviceName')
      : i18n.t('outdoorDeviceReport.headers.storeName');
  }

  normalizeData(
    metrics: Record<string, number>,
    from: string,
    to: string
  ): Record<string, number> {
    const normalized: Record<string, number> = {};
    let fromMomentObj = moment(from);
    const toMomentObj = moment(to);
    while (fromMomentObj <= toMomentObj) {
      const dateKey = fromMomentObj.format('YYYY-MM-DD');
      const hourStr = fromMomentObj.format('HH');
      const timeKey = `${dateKey}T${hourStr}`;
      normalized[timeKey] = metrics[timeKey] ?? 0;
      fromMomentObj = fromMomentObj.clone().add(1, 'hour');
    }
    return normalized;
  }

  getColumns(
    tableSummaryRecord: OutdoorDeviceReportTableRecord | undefined,
    from: string,
    to: string
  ): ColumnDefinition[] {
    const columns: ColumnDefinition[] = [
      {
        ...getDefaultColumnDescription(
          KEY_COLUMN,
          this.deliveryTargetNameHeader,
          true
        ),
        footer: tableSummaryRecord ? tableSummaryRecord[KEY_COLUMN] : undefined,
        classes: () => styles.storeName,
        headerClasses: () => styles.storeName,
        footerClasses: () => styles.storeName
      }
    ];
    let fromMomentObj = moment(from);
    const toMomentObj = moment(to);
    while (fromMomentObj <= toMomentObj) {
      const dateKey = fromMomentObj.format('YYYY-MM-DD');
      const hourStr = fromMomentObj.format('HH');
      const timeKey = `${dateKey}T${hourStr}`;
      const columnLabel = `${dateKey} ${hourLabelValueMap[fromMomentObj.hour()] || hourStr}`;
      columns.push({
        ...getDefaultColumnDescription(timeKey, columnLabel, false),
        footer: tableSummaryRecord
          ? commaDecorator(tableSummaryRecord[timeKey])
          : undefined
      });
      fromMomentObj = fromMomentObj.clone().add(1, 'hour');
    }
    return columns;
  }
  getSummaryRecord(
    records: OutdoorDeviceReportTableRecord[],
    from: string,
    to: string
  ) {
    const hourTotals: Record<string, number> = {};
    records.forEach((record) => {
      let fromMomentObj = moment(from);
      const toMomentObj = moment(to);
      while (fromMomentObj <= toMomentObj) {
        const dateKey = fromMomentObj.format('YYYY-MM-DD');
        const hourStr = fromMomentObj.format('HH');
        const timeKey = `${dateKey}T${hourStr}`;
        hourTotals[timeKey] =
          (hourTotals[timeKey] || 0) + (record[timeKey] || 0);
        fromMomentObj = fromMomentObj.clone().add(1, 'hour');
      }
    });
    return {
      [KEY_COLUMN]: i18n.t('reportTable.footers.total'),
      ...hourTotals
    } as OutdoorDeviceReportTableRecord;
  }
  getChartData(
    tableSummaryRecord: OutdoorDeviceReportTableRecord | undefined,
    metricType: string
  ) {
    if (!tableSummaryRecord) return [];
    const hourTotals: Record<string, number> = {};
    Object.keys(tableSummaryRecord)
      .filter((key) => key !== KEY_COLUMN)
      .forEach((key) => {
        const hourStr = key.split('T')[1];
        const hour = String(parseInt(hourStr, 10));
        hourTotals[hour] =
          (hourTotals[hour] || 0) + (tableSummaryRecord[key] || 0);
      });
    return Object.keys(hourTotals).map((key) => ({
      name: hourLabelValueMap[key] || key,
      [metricType]: hourTotals[key]
    }));
  }
  getCsvContent(
    records: OutdoorDeviceReportTableRecord[],
    from: string,
    to: string
  ): OutdoorDeviceReportCsvContent {
    const timeKeys: string[] = [];
    const headers: string[] = [this.deliveryTargetNameHeader];
    let fromMomentObj = moment(from);
    const toMomentObj = moment(to);
    while (fromMomentObj <= toMomentObj) {
      const dateKey = fromMomentObj.format('YYYY-MM-DD');
      timeKeys.push(`${dateKey}T${fromMomentObj.format('HH')}`);
      headers.push(`${dateKey} ${csvHourLabelValueMap[fromMomentObj.hour()]}`);
      fromMomentObj = fromMomentObj.clone().add(1, 'hour');
    }
    return {
      headers,
      rows: records.map((record) => [
        record[KEY_COLUMN],
        ...timeKeys.map((timeKey) => record[timeKey])
      ])
    };
  }
}
